import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';

export interface IUser {
  userId: string;
  username: string;
  idpId: string;
  email: string;
  firstName: string;
  familyName: string;
  phone: string;
  avatar: string | null;
  status: string;
  createdAt: string;
  businessRoll: string[];
  activationCode: string;
  activationRetryCount: number;
  plan: string | null;
  role: string;
}

export interface ISubscription {
  subscriptionId: string;
  name: string;
  baseValue: string;
  status: string;
  order: string;
  isCorporate: boolean | null;
}

export interface OrganizationSubscription {
  activatedAt: string;
  activeSubscription: ISubscription;
  subscriptions: ISubscription[];
  organizationId: string | undefined;
  requestedSubscriptionId: string;
}

export interface IOrganization {
  organizationId: string;
  name: string;
  country: string;
  website: string;
  ownDevice: boolean;
  thirdPartyDevice: boolean;
  nextBillingDate: string;
  baseSubscriptionAmount: string;
  isContacted: boolean;
  status: string;
  stripeAccountId: string;
  subscription: ISubscription;
  users: IUser[];
  createdBy: IUser;
}

export interface IGetOrganizationList {
  name: string;
  organizationId: string;
}

export interface IOrganizationSlice {
  componentStatus: { [name: string]: undefined | string | boolean };
  organization: IOrganization | null;
  error: string | null;
  organizationSubscription: OrganizationSubscription | null;
  updateOrganizationLoading: boolean;
  subscriptionUpdateSucess: boolean;
  organizationList?: IGetOrganizationList[];
}

const initialState: IOrganizationSlice = {
  componentStatus: {},
  organization: null,
  error: null,
  organizationSubscription: null,
  updateOrganizationLoading: false,
  subscriptionUpdateSucess: false,
};

const organizationSlice = createSlice({
  name: 'organization',
  initialState,
  reducers: {
    setComponentStatus(
      state: IOrganizationSlice,
      action: PayloadAction<{ [key: string]: undefined | string | boolean }>,
    ) {
      state.componentStatus = { ...state.componentStatus, ...action.payload };
    },
    clearComponentStatus(state: IOrganizationSlice, action: PayloadAction<string[] | undefined>) {
      if (action.payload?.length) {
        state.componentStatus = _.omit(state.componentStatus, action.payload);
      } else {
        state.componentStatus = {};
      }
    },
    getOrganizationByIdAsync(_state: IOrganizationSlice, _action: PayloadAction<string>) {},
    setOrganizationList: (state: IOrganizationSlice, action: PayloadAction<IGetOrganizationList[]>) => {
      state.organizationList = [...action.payload];
    },
    getOrganizationListAsync(_state: IOrganizationSlice) {},

    getOrganizationSubscriptionAsync(_state: IOrganizationSlice) {},
    setOrganizationSubscription(state: IOrganizationSlice, action: PayloadAction<any>) {
      state.organizationSubscription = action.payload;
    },
    fetchOrganizationByIdStart(state: IOrganizationSlice) {
      state.error = null;
    },
    fetchOrganizationByIdSuccess(state: IOrganizationSlice, action: PayloadAction<IOrganization>) {
      state.organization = action.payload;
      state.error = null;
    },
    fetchOrganizationByIdFailure(state: IOrganizationSlice, action: PayloadAction<string>) {
      state.error = action.payload;
    },
    updateOrganizationSubscriptionStart(
      state: IOrganizationSlice,
      _action: PayloadAction<{ organizationId: string; data: any; currentSubscription: ISubscription }>,
    ) {
      state.error = null;
      state.componentStatus = { ...state.componentStatus };
      state.updateOrganizationLoading = true;
      state.subscriptionUpdateSucess = false;
    },
    updateOrganizationSubscriptionSuccess(state: IOrganizationSlice, action: PayloadAction<IOrganization>) {
      state.organization = action.payload;
      state.componentStatus = { ...state.componentStatus };
      state.error = null;
      state.updateOrganizationLoading = false;
      state.subscriptionUpdateSucess = true;
    },
    updateOrganizationSubscriptionFailure(state: IOrganizationSlice, action: PayloadAction<string>) {
      state.componentStatus = { ...state.componentStatus };
      state.error = action.payload;
      state.updateOrganizationLoading = false;
      state.subscriptionUpdateSucess = false;
    },
    resetSubscriptionUpdateSuccess(state: IOrganizationSlice) {
      state.subscriptionUpdateSucess = false;
    },
  },
});

export const {
  setComponentStatus,
  clearComponentStatus,
  fetchOrganizationByIdStart,
  fetchOrganizationByIdSuccess,
  fetchOrganizationByIdFailure,
  updateOrganizationSubscriptionStart,
  updateOrganizationSubscriptionSuccess,
  updateOrganizationSubscriptionFailure,
} = organizationSlice.actions;

export const { actions: organizationActions, reducer: organizationReducer } = organizationSlice;
